<div [class]="embedded() ? 'w-full' : 'w-full max-w-6xl mx-auto p-6'">
  @if (error()) {
    <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-2">
      <i class="pi pi-exclamation-triangle text-red-600"></i>
      <p class="text-red-800">{{ error() }}</p>
    </div>
  }

  <div class="relative">
    @if (formDataLoading()) {
      <div
        class="absolute inset-0 z-40 flex items-start justify-center rounded-xl bg-slate-400/25 backdrop-blur-[2px] pt-24"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div class="flex flex-col items-center gap-3 rounded-2xl border border-slate-300/80 bg-white/95 px-8 py-6 shadow-lg">
          <i class="pi pi-spin pi-spinner text-3xl text-sky-600"></i>
          <p class="text-sm font-semibold text-slate-800">Cargando cliente…</p>
        </div>
      </div>
    }

  <form [formGroup]="customerForm" (ngSubmit)="onSubmit()">
    <p-card styleClass="sale-form-card">
      <ng-template pTemplate="header">
        <div class="px-4 py-3 border-b border-slate-200 bg-slate-50/50">
          <div class="flex items-center justify-between gap-3">
            <div class="flex items-center gap-3 min-w-0">
              @if (!embedded()) {
                <p-button
                  icon="pi pi-arrow-left"
                  [rounded]="true"
                  [text]="true"
                  severity="secondary"
                  type="button"
                  (onClick)="cancel()"
                  pTooltip="Volver al listado"
                  tooltipPosition="bottom"
                />
              }
              <h2 class="text-lg font-semibold text-slate-900 flex items-center gap-2 min-w-0">
                <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                  <i class="pi pi-users"></i>
                </span>
                <span class="truncate">{{ isEditMode() ? 'Editar cliente' : 'Nuevo cliente' }}</span>
              </h2>
            </div>
            @if (editingCustomerId(); as customerId) {
              <p-button
                icon="pi pi-refresh"
                [rounded]="true"
                [text]="true"
                severity="secondary"
                type="button"
                (onClick)="refreshCustomer()"
                [loading]="loading()"
                [disabled]="loading()"
                pTooltip="Actualizar cliente"
                tooltipPosition="bottom"
              />
            }
          </div>
        </div>
        <div class="flex flex-wrap items-center justify-end gap-2 border-b border-slate-200 bg-slate-50/90 px-3 py-2">
          <div class="inline-flex items-center gap-2">
            <div class="inline-flex items-baseline gap-x-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
              <span class="text-[11px] font-semibold tracking-wider text-slate-500">STATUS:</span>
              <span
                class="text-sm font-medium font-mono"
                [class.text-slate-900]="!isInactiveCustomer()"
                [class.text-red-700]="isInactiveCustomer()"
              >{{ statusDisplayLabel() }}</span>
              <i
                class="pi pi-question-circle text-slate-400 cursor-help text-xs shrink-0"
                [pTooltip]="statusTooltip"
                tooltipPosition="bottom"
              ></i>
            </div>
            @if (isInactiveCustomer()) {
              <p-button
                label="Reactivar"
                icon="pi pi-replay"
                size="small"
                severity="success"
                type="button"
                [loading]="reactivating()"
                [disabled]="loading() || reactivating() || formDataLoading()"
                (onClick)="reactivateCustomer()"
                appDisableUnless="CUSTOMER_WRITE"
                pTooltip="Volver a activar este cliente"
                tooltipPosition="bottom"
              />
            }
          </div>
        </div>
      </ng-template>
      <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)" class="">
        <p-tablist>
          @for (tab of tabs; track tab.value) {
            <p-tab
              [value]="tab.value"
              [disabled]="tab.value !== 'generales' && !editingCustomerId()"
              class="flex items-center gap-2! text-inherit"
            >
              <i [class]="tab.icon"></i>
              <span>{{ tab.label }}</span>
            </p-tab>
          }
        </p-tablist>
        <p-tabpanels>
          <!-- Tab Generales -->
          <p-tabpanel value="generales">
            <div class="mt-4 mb-4">
              <h3 class="text-lg font-semibold text-slate-900">Información Básica</h3>
              <p class="text-sm text-slate-500">Datos generales del cliente.</p>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mt-4">
              <div class="flex flex-col gap-1">
                <p-floatlabel variant="on">
                  <input pInputText
                    id="legalName"
                    formControlName="legalName"
                    [maxlength]="maxLength.legalName"
                    fluid
                    autocomplete="off"
                    (blur)="onLegalNameBlur()"
                    [invalid]="(customerForm.get('legalName')?.invalid ?? false) && (customerForm.get('legalName')?.touched ?? false)"
                  />
                  <label for="legalName">Nombre o Razón Social <span class="text-red-500">*</span></label>
                </p-floatlabel>
                @if (customerForm.get('legalName')?.hasError('required') && customerForm.get('legalName')?.touched) {
                  <small class="text-red-600">Campo requerida</small>
                }
                @if (customerForm.get('legalName')?.hasError('maxlength')) {
                  <small class="text-red-600">Máximo {{ maxLength.legalName }} caracteres</small>
                }
              </div>

              <div class="flex flex-col gap-1">
                <p-floatlabel variant="on">
                  <input pInputText
                    id="comercialName"
                    formControlName="comercialName"
                    [maxlength]="maxLength.comercialName"
                    fluid
                    autocomplete="off"
                    [invalid]="(customerForm.get('comercialName')?.invalid ?? false) && (customerForm.get('comercialName')?.touched ?? false)"
                  />
                  <label for="comercialName">Nombre Comercial <span class="text-red-500">*</span></label>
                </p-floatlabel>
                @if (customerForm.get('comercialName')?.hasError('required') && customerForm.get('comercialName')?.touched) {
                  <small class="text-red-600">El nombre comercial es requerido</small>
                }
                @if (customerForm.get('comercialName')?.hasError('maxlength')) {
                  <small class="text-red-600">Máximo {{ maxLength.comercialName }} caracteres</small>
                }
              </div>

              <div class="flex flex-col gap-1">
                <p-floatlabel variant="on">
                  <input pInputText
                    id="email"
                    type="email"
                    formControlName="email"
                    [maxlength]="maxLength.email"
                    fluid
                    autocomplete="off"
                    [invalid]="(customerForm.get('email')?.invalid ?? false) && (customerForm.get('email')?.touched ?? false)"
                  />
                  <label for="email">Email <span class="text-red-500">*</span></label>
                </p-floatlabel>
                @if (customerForm.get('email')?.hasError('required') && customerForm.get('email')?.touched) {
                  <small class="text-red-600">El email es requerido</small>
                }
                @if (customerForm.get('email')?.hasError('email') && customerForm.get('email')?.touched) {
                  <small class="text-red-600">El formato del email no es correcto</small>
                }
                @if (customerForm.get('email')?.hasError('maxlength')) {
                  <small class="text-red-600">Máximo {{ maxLength.email }} caracteres</small>
                }
              </div>

              <div class="flex flex-col gap-1">
                <p-floatlabel variant="on">
                  <input pInputText
                    id="phone"
                    formControlName="phone"
                    type="tel"
                    inputmode="numeric"
                    [maxlength]="maxLength.phone"
                    fluid
                    autocomplete="off"
                    (input)="onPhoneInput($event)"
                    [invalid]="(customerForm.get('phone')?.invalid ?? false) && (customerForm.get('phone')?.touched ?? false)"
                  />
                  <label for="phone">Teléfono</label>
                </p-floatlabel>
                @if (customerForm.get('phone')?.hasError('maxlength')) {
                  <small class="text-red-600">Máximo {{ maxLength.phone }} dígitos</small>
                }
                @if (customerForm.get('phone')?.hasError('pattern') && customerForm.get('phone')?.touched) {
                  <small class="text-red-600">Debe tener exactamente {{ maxLength.phone }} dígitos</small>
                }
              </div>
            </div>

            <div class="mt-8 border-t border-slate-200 pt-6">
              <div class="mb-4">
                <h3 class="text-lg font-semibold text-slate-900">Datos fiscales</h3>
                <p class="text-sm text-slate-500">Opcionales al guardar. Son necesarios para timbrar una factura.</p>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div class="flex flex-col gap-1">
                  <p-floatlabel class="w-full" variant="on">
                    <p-select
                      formControlName="type"
                      inputId="type"
                      [options]="CompanyTypeOptions"
                      optionLabel="label"
                      optionValue="value"
                      [showClear]="true"
                      class="w-full"
                    />
                    <label for="type">Física/Moral</label>
                  </p-floatlabel>
                </div>

                <div class="flex flex-col gap-1">
                  <p-floatlabel class="w-full" variant="on">
                    <p-select
                      formControlName="taxRegimeId"
                      inputId="taxRegime"
                      [options]="taxRegimeOptions()"
                      optionLabel="description"
                      optionValue="id"
                      [filter]="true"
                      filterPlaceholder="Buscar..."
                      [showClear]="true"
                      class="w-full"
                    />
                    <label for="taxRegime">Régimen Fiscal</label>
                  </p-floatlabel>
                </div>

                <div class="flex flex-col gap-1">
                  <p-floatlabel variant="on">
                    <input pInputText
                      id="taxId"
                      formControlName="taxId"
                      [maxlength]="maxLength.taxId"
                      fluid
                      autocomplete="off"
                      class="uppercase"
                      (input)="onTaxIdInput($event)"
                      [invalid]="(customerForm.get('taxId')?.invalid ?? false) && (customerForm.get('taxId')?.touched ?? false)"
                    />
                    <label for="taxId">RFC</label>
                  </p-floatlabel>
                  @if (customerForm.get('taxId')?.hasError('maxlength')) {
                    <small class="text-red-600">Máximo {{ maxLength.taxId }} caracteres</small>
                  }
                </div>

                <div class="flex flex-col gap-1">
                  <p-floatlabel class="w-full" variant="on">
                    <p-select
                      formControlName="countryId"
                      inputId="countryId"
                      [options]="countries()"
                      optionLabel="name"
                      optionValue="id"
                      [showClear]="true"
                      class="w-full"
                    />
                    <label for="countryId">País</label>
                  </p-floatlabel>
                </div>
              </div>
            </div>

            <!-- Términos de pago -->
            <div class="mt-8 border-t border-slate-200 pt-6">
              <div class="mb-4">
                <h3 class="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <i class="pi pi-wallet"></i>
                  Términos de pago
                </h3>
                <p class="text-sm text-slate-500">Plazo de pago por defecto para este cliente.</p>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div class="flex flex-col gap-1">
                  <p-floatlabel class="w-full" variant="on">
                    <p-inputNumber
                      inputId="daysUntilExpiry"
                      formControlName="daysUntilExpiry"
                      [min]="0"
                      [max]="999"
                      [maxlength]="maxLength.daysUntilExpiry"
                      [useGrouping]="false"
                      [minFractionDigits]="0"
                      [maxFractionDigits]="0"
                      fluid
                      [placeholder]="daysUntilExpiryPlaceholder()"
                      [invalid]="(customerForm.get('daysUntilExpiry')?.invalid ?? false) && (customerForm.get('daysUntilExpiry')?.touched ?? false)"
                    />
                    <label for="daysUntilExpiry">Plazo de pago (días)</label>
                  </p-floatlabel>
                  @if (customerForm.get('daysUntilExpiry')?.hasError('nonNegativeInteger') && customerForm.get('daysUntilExpiry')?.touched) {
                    <small class="text-red-600">Indica un entero mayor o igual a 0.</small>
                  }
                  @if (customerForm.get('daysUntilExpiry')?.hasError('maxlength') && customerForm.get('daysUntilExpiry')?.touched) {
                    <small class="text-red-600">Máximo {{ maxLength.daysUntilExpiry }} dígitos</small>
                  }
                </div>
              </div>
            </div>
          </p-tabpanel>

          <!-- Tab Direcciones -->
          <p-tabpanel value="direcciones">
            @if (!editingCustomerId()) {
              <p class="text-slate-500 py-6">Guarda el cliente para habilitar direcciones.</p>
            } @else {
              <div class="flex items-center justify-between mt-4 mb-4">
                <div>
                  <h3 class="text-lg font-semibold text-slate-900">Direcciones</h3>
                  <p class="text-sm text-slate-500">Agrega o edita direcciones del cliente.</p>
                </div>
                <p-button label="Nuevo" icon="pi pi-plus" (onClick)="newAddress()" />
              </div>
              <div class="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                <div class="overflow-x-auto">
                  <table class="w-full">
                    <thead class="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Tipo</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Dirección</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Municipio / Estado / CP</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">País</th>
                        <th class="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-slate-200">
                      @if (addresses().length === 0) {
                        <tr>
                          <td class="px-6 py-6 text-center text-sm text-slate-500" colspan="5">No hay direcciones registradas.</td>
                        </tr>
                      }
                      @for (address of addresses(); track address.id) {
                        <tr class="hover:bg-slate-50 transition-colors">
                          <td class="px-6 py-4 whitespace-nowrap">
                            <span class="flex items-center gap-2 text-sm font-medium text-slate-900">
                              <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-100" style="color: var(--p-primary-500)">
                                <i [class]="'pi ' + getAddressTypeIcon(address.type)"></i>
                              </span>
                              {{ getAddressTypeLabel(address.type) }}
                            </span>
                          </td>
                          <td class="px-6 py-4">
                            <div class="text-sm text-slate-900">{{ formatAddressStreet(address) }}</div>
                          </td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                            {{ formatAddressLocality(address) }}
                          </td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{{ address.country?.name ?? address.country?.code ?? '-' }}</td>
                          <td class="px-6 py-4 whitespace-nowrap text-right">
                            <p-button icon="pi pi-pen-to-square" [rounded]="true" [text]="true" severity="secondary" styleClass="hover:!bg-sky-100" (onClick)="startEditAddress(address)" />
                            <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger" (onClick)="openDeleteAddress(address)" />
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            }
          </p-tabpanel>

          <!-- Tab Contactos -->
          <p-tabpanel value="contactos">
            @if (!editingCustomerId()) {
              <p class="text-slate-500 py-6">Guarda el cliente para habilitar contactos.</p>
            } @else {
              <div class="flex items-center justify-between mt-4 mb-4">
                <div>
                  <h3 class="text-lg font-semibold text-slate-900">Contactos</h3>
                  <p class="text-sm text-slate-500">Agrega o edita contactos del cliente.</p>
                </div>
                <p-button label="Nuevo" icon="pi pi-plus" (onClick)="newContact()" />
              </div>
              <div class="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                <div class="overflow-x-auto">
                  <table class="w-full">
                    <thead class="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Tipo</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Nombre</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Email</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Teléfono</th>
                        <th class="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-slate-200">
                      @if (contacts().length === 0) {
                        <tr>
                          <td class="px-6 py-6 text-center text-sm text-slate-500" colspan="5">No hay contactos registrados.</td>
                        </tr>
                      }
                      @for (contact of contacts(); track contact.id) {
                        <tr class="hover:bg-slate-50 transition-colors">
                          <td class="px-6 py-4 whitespace-nowrap">
                            <span class="flex items-center gap-2 text-sm font-medium text-slate-900">
                              <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-100" style="color: var(--p-primary-500)">
                                <i [class]="'pi ' + getContactTypeIcon(contact.type)"></i>
                              </span>
                              {{ getContactTypeLabel(contact.type) }}
                            </span>
                          </td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{{ contact.conactName }}</td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{{ contact.email || '-' }}</td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{{ contact.phone || '-' }}</td>
                          <td class="px-6 py-4 whitespace-nowrap text-right">
                            <p-button icon="pi pi-pen-to-square" [rounded]="true" [text]="true" severity="secondary" styleClass="hover:!bg-sky-100" (onClick)="startEditContact(contact)" />
                            <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger" (onClick)="openDeleteContact(contact)" />
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            }
          </p-tabpanel>

          <!-- Tab Adjuntos -->
          <p-tabpanel value="adjuntos">
            @if (!editingCustomerId()) {
              <p class="text-slate-500 py-6">Guarda el cliente para habilitar adjuntos.</p>
            } @else {
              <div class="flex items-center justify-between mt-4 mb-4">
                <div>
                  <h3 class="text-lg font-semibold text-slate-900">Adjuntos</h3>
                  <p class="text-sm text-slate-500">Sube documentos relacionados con el cliente (máximo 10 MB por archivo).</p>
                </div>
                <div>
                  <input #attachmentInput type="file" class="hidden" multiple (change)="onAttachmentSelect($event)" />
                  <p-button
                    type="button"
                    [label]="uploadingAttachments() ? 'Subiendo...' : 'Subir archivo'"
                    [icon]="uploadingAttachments() ? 'pi pi-spin pi-spinner' : 'pi pi-upload'"
                    [loading]="uploadingAttachments()"
                    [disabled]="uploadingAttachments()"
                    (onClick)="attachmentInput.click()"
                  />
                </div>
              </div>
              <div class="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                <div class="overflow-x-auto">
                  <table class="w-full">
                    <thead class="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Archivo</th>
                        <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider hidden sm:table-cell">Tipo</th>
                        <th class="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Tamaño</th>
                        <th class="px-6 py-3 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider hidden sm:table-cell">Fecha</th>
                        <th class="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-slate-200">
                      @if (attachmentsLoading()) {
                        <tr>
                          <td class="px-6 py-8 text-center text-sm text-slate-500" colspan="5">
                            <i class="pi pi-spin pi-spinner mr-2"></i>Cargando adjuntos...
                          </td>
                        </tr>
                      } @else if (attachments().length === 0) {
                        <tr>
                          <td class="px-6 py-6 text-center text-sm text-slate-500" colspan="5">No hay archivos adjuntos.</td>
                        </tr>
                      } @else {
                        @for (file of attachments(); track file.id) {
                          <tr class="hover:bg-slate-50 transition-colors">
                            <td class="px-6 py-4">
                              <span class="flex items-center gap-2 text-sm font-medium text-slate-900">
                                <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-100" style="color: var(--p-primary-500)">
                                  <i [class]="'pi ' + getFileIcon(file.contentType)"></i>
                                </span>
                                <span class="truncate max-w-xs">{{ file.name }}</span>
                              </span>
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600 hidden sm:table-cell">{{ file.contentType || '-' }}</td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600 text-right">{{ formatFileSize(file.size) }}</td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-600 text-center hidden sm:table-cell">
                              {{ file.createdAt ? (file.createdAt | date:'dd/MM/yyyy HH:mm') : '-' }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-right">
                              <p-button icon="pi pi-download" [rounded]="true" [text]="true" severity="secondary" styleClass="hover:!bg-sky-100" (onClick)="openAttachment(file)" />
                              <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger" (onClick)="openDeleteAttachment(file)" />
                            </td>
                          </tr>
                        }
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            }
          </p-tabpanel>

          <p-tabpanel value="auditoria">
            @if (activeTab() === 'auditoria') {
              <app-entity-audit-panel
                entityType="Customer"
                [entityId]="editingCustomerId()"
                entityLabel="cliente"
              />
            }
          </p-tabpanel>
        </p-tabpanels>
      </p-tabs>

      <ng-template #footer >
        <div class="!mt-4 flex justify-end gap-4 mt-1 !p-4 !-m-5 bg-gray-50 p-2 border-t border-slate-200">
          <p-button label="Cancelar" severity="secondary" (onClick)="cancel()" [loading]="loading()" type="button" />
          <p-button
            [label]="loading() ? 'Guardando...' : (isEditMode() ? 'Actualizar Cliente' : 'Guardar Cliente')"
            [loading]="loading()"
            [disabled]="customerForm.invalid || loading()"
            type="submit"
            appDisableUnless="CUSTOMER_WRITE"
          />
        </div>
      </ng-template>
    </p-card>
  </form>
  </div>

  <!-- Modal Dirección -->
  @if (showAddressForm()) {
    <div class="fixed inset-0 z-[1200] flex items-center justify-center">
      <div
        class="absolute inset-0 bg-black transition-opacity duration-200"
        [class.bg-opacity-40]="addressModalShown()"
        [class.bg-opacity-0]="!addressModalShown()"
        (click)="cancelAddressEdit()"
      ></div>
      <div
        class="relative bg-white rounded-xl shadow-xl w-full max-w-lg p-6 z-10 transform transition-all duration-200"
        [class.opacity-100]="addressModalShown()"
        [class.opacity-0]="!addressModalShown()"
        [class.scale-100]="addressModalShown()"
        [class.scale-95]="!addressModalShown()"
      >
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-slate-900">
            {{ editingAddressId() ? 'Editar dirección' : 'Nueva dirección' }}
          </h3>
          <p-button icon="pi pi-times" [rounded]="true" [text]="true" severity="secondary" (onClick)="cancelAddressEdit()" />
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4" [formGroup]="addressForm">
          <!-- Tipo 50% -->
          <div class="flex flex-col gap-1 md:col-span-2 md:w-1/2">
            <p-floatlabel class="w-full" variant="on">
              <p-select
                formControlName="addressType"
                inputId="addressType"
                [options]="addressTypes"
                optionLabel="label"
                optionValue="value"
                class="w-full"
                [invalid]="(addressForm.get('addressType')?.invalid ?? false) && (addressForm.get('addressType')?.touched ?? false)"
              />
              <label for="addressType">Tipo <span class="text-red-500">*</span></label>
            </p-floatlabel>
            @if (addressForm.get('addressType')?.hasError('required') && addressForm.get('addressType')?.touched) {
              <small class="text-red-600">Campo obligatorio</small>
            }
          </div>

          <!-- Calle 100% -->
          <div class="flex flex-col gap-1 md:col-span-2">
            <p-floatlabel variant="on">
              <input
                pInputText
                id="addressStreet"
                formControlName="street"
                [maxlength]="maxLength.street"
                fluid
                autocomplete="off"
                [invalid]="(addressForm.get('street')?.invalid ?? false) && (addressForm.get('street')?.touched ?? false)"
              />
              <label for="addressStreet">Calle <span class="text-red-500">*</span></label>
            </p-floatlabel>
            @if (addressForm.get('street')?.hasError('required') && addressForm.get('street')?.touched) {
              <small class="text-red-600">Campo obligatorio</small>
            }
            @if (addressForm.get('street')?.hasError('maxlength')) {
              <small class="text-red-600">Máximo {{ maxLength.street }} caracteres</small>
            }
          </div>

          <!-- No. Ext 50% -->
          <div class="flex flex-col gap-1">
            <p-floatlabel variant="on">
              <input
                pInputText
                id="addressExternalNo"
                formControlName="externalNo"
                [maxlength]="maxLength.externalNo"
                fluid
                autocomplete="off"
                [invalid]="(addressForm.get('externalNo')?.invalid ?? false) && (addressForm.get('externalNo')?.touched ?? false)"
              />
              <label for="addressExternalNo">No. Ext <span class="text-red-500">*</span></label>
            </p-floatlabel>
            @if (addressForm.get('externalNo')?.hasError('required') && addressForm.get('externalNo')?.touched) {
              <small class="text-red-600">Campo obligatorio</small>
            }
            @if (addressForm.get('externalNo')?.hasError('maxlength')) {
              <small class="text-red-600">Máximo {{ maxLength.externalNo }} caracteres</small>
            }
          </div>

          <!-- No. Int 50% -->
          <div class="flex flex-col gap-1">
            <p-floatlabel variant="on">
              <input
                pInputText
                id="addressInternalNo"
                formControlName="internalNo"
                [maxlength]="maxLength.internalNo"
                fluid
                autocomplete="off"
                [invalid]="(addressForm.get('internalNo')?.invalid ?? false) && (addressForm.get('internalNo')?.touched ?? false)"
              />
              <label for="addressInternalNo">No. Int</label>
            </p-floatlabel>
            @if (addressForm.get('internalNo')?.hasError('maxlength')) {
              <small class="text-red-600">Máximo {{ maxLength.internalNo }} caracteres</small>
            }
          </div>

          <!-- CP 50% -->
          <div class="flex flex-col gap-1">
            <p-floatlabel variant="on">
              <input
                pInputText
                id="addressZipCode"
                formControlName="zipCode"
                [maxlength]="maxLength.zipCode"
                fluid
                type="tel"
                inputmode="numeric"
                autocomplete="off"
                (input)="onZipCodeInput($event)"
                (blur)="onZipCodeBlur()"
                [invalid]="(addressForm.get('zipCode')?.invalid ?? false) && (addressForm.get('zipCode')?.touched ?? false)"
              />
              <label for="addressZipCode">CP <span class="text-red-500">*</span></label>
            </p-floatlabel>
            @if (postalCodeLoading()) {
              <small class="text-slate-500">Consultando código postal...</small>
            }
            @if (addressForm.get('zipCode')?.hasError('required') && addressForm.get('zipCode')?.touched) {
              <small class="text-red-600">Campo obligatorio</small>
            }
            @if (addressForm.get('zipCode')?.hasError('pattern') && addressForm.get('zipCode')?.touched && !addressForm.get('zipCode')?.hasError('required')) {
              <small class="text-red-600">Debe tener exactamente {{ maxLength.zipCode }} dígitos</small>
            }
          </div>

          <!-- Colonia 50% -->
          <div class="flex flex-col gap-1">
            @if (isMexicoSelected()) {
              <p-floatlabel class="w-full" variant="on">
                <p-select
                  formControlName="colonia"
                  inputId="addressColonia"
                  [options]="coloniaOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [filter]="true"
                  filterPlaceholder="Buscar colonia..."
                  class="w-full"
                  [invalid]="(addressForm.get('colonia')?.invalid ?? false) && (addressForm.get('colonia')?.touched ?? false)"
                />
                <label for="addressColonia">Colonia <span class="text-red-500">*</span></label>
              </p-floatlabel>
            } @else {
              <p-floatlabel variant="on">
                <input
                  pInputText
                  id="addressColonia"
                  formControlName="colonia"
                  [maxlength]="maxLength.colonia"
                  fluid
                  autocomplete="off"
                  [invalid]="(addressForm.get('colonia')?.invalid ?? false) && (addressForm.get('colonia')?.touched ?? false)"
                />
                <label for="addressColonia">Colonia <span class="text-red-500">*</span></label>
              </p-floatlabel>
            }
            @if (addressForm.get('colonia')?.hasError('required') && addressForm.get('colonia')?.touched) {
              <small class="text-red-600">Campo obligatorio</small>
            }
            @if (addressForm.get('colonia')?.hasError('maxlength')) {
              <small class="text-red-600">Máximo {{ maxLength.colonia }} caracteres</small>
            }
          </div>

          <!-- Municipio auto 50% -->
          <div class="flex flex-col gap-1">
            <p-floatlabel variant="on">
              <input
                pInputText
                id="addressMunicipality"
                [value]="municipalityDisplay()"
                [readonly]="true"
                fluid
                autocomplete="off"
                [invalid]="(addressForm.get('municipality')?.invalid ?? false) && ((addressForm.get('municipality')?.touched ?? false) || (addressForm.get('zipCode')?.touched ?? false))"
              />
              <label for="addressMunicipality">Municipio <span class="text-red-500">*</span></label>
            </p-floatlabel>
            @if (addressForm.get('municipality')?.hasError('required') && (addressForm.get('municipality')?.touched || addressForm.get('zipCode')?.touched)) {
              <small class="text-red-600">Consulta un CP válido para autocompletar</small>
            }
          </div>

          <!-- País 50% (México por defecto) -->
          <div class="flex flex-col gap-1">
            <p-floatlabel class="w-full" variant="on">
              <p-select
                formControlName="addressCountry"
                inputId="addressCountry"
                [options]="countries()"
                optionLabel="name"
                optionValue="code"
                class="w-full"
                [invalid]="(addressForm.get('addressCountry')?.invalid ?? false) && (addressForm.get('addressCountry')?.touched ?? false)"
              />
              <label for="addressCountry">País <span class="text-red-500">*</span></label>
            </p-floatlabel>
            @if (addressForm.get('addressCountry')?.hasError('required') && addressForm.get('addressCountry')?.touched) {
              <small class="text-red-600">Campo obligatorio</small>
            }
          </div>
        </div>
        <div class="flex justify-end gap-3 mt-6">
          <p-button label="Cancelar" severity="secondary" (onClick)="cancelAddressEdit()" />
          <p-button
            [label]="relationsLoading() ? 'Guardando...' : (editingAddressId() ? 'Actualizar' : 'Guardar')"
            icon="pi pi-check"
            [loading]="relationsLoading()"
            (onClick)="saveAddress()"
          />
        </div>
      </div>
    </div>
  }

  <!-- Modal Contacto -->
  @if (showContactForm()) {
    <div class="fixed inset-0 z-[1200] flex items-center justify-center">
      <div
        class="absolute inset-0 bg-black transition-opacity duration-200"
        [class.bg-opacity-40]="contactModalShown()"
        [class.bg-opacity-0]="!contactModalShown()"
        (click)="cancelContactEdit()"
      ></div>
      <div
        class="relative bg-white rounded-xl shadow-xl w-full max-w-lg p-6 z-10 transform transition-all duration-200"
        [class.opacity-100]="contactModalShown()"
        [class.opacity-0]="!contactModalShown()"
        [class.scale-100]="contactModalShown()"
        [class.scale-95]="!contactModalShown()"
      >
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-slate-900">
            {{ editingContactId() ? 'Editar contacto' : 'Nuevo contacto' }}
          </h3>
          <p-button icon="pi pi-times" [rounded]="true" [text]="true" severity="secondary" (onClick)="cancelContactEdit()" />
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4" [formGroup]="contactForm">
          <div class="flex flex-col gap-1">
            <p-floatlabel class="w-full" variant="on">
              <p-select
                formControlName="contactType"
                inputId="contactType"
                [options]="contactTypes"
                optionLabel="label"
                optionValue="value"
                class="w-full"
                [invalid]="(contactForm.get('contactType')?.invalid ?? false) && (contactForm.get('contactType')?.touched ?? false)"
              />
              <label for="contactType">Tipo <span class="text-red-500">*</span></label>
            </p-floatlabel>
            @if (contactForm.get('contactType')?.hasError('required') && contactForm.get('contactType')?.touched) {
              <small class="text-red-600">Campo obligatorio</small>
            }
          </div>
          <div class="flex flex-col gap-1 col-span-2">
            <p-floatlabel variant="on">
              <input pInputText id="contactName" formControlName="conactName" [maxlength]="maxLength.contactName" fluid autocomplete="off"
                [invalid]="(contactForm.get('conactName')?.invalid ?? false) && (contactForm.get('conactName')?.touched ?? false)" />
              <label for="contactName">Nombre del Contacto <span class="text-red-500">*</span></label>
            </p-floatlabel>
            @if (contactForm.get('conactName')?.hasError('required') && contactForm.get('conactName')?.touched) {
              <small class="text-red-600">Campo obligatorio</small>
            }
            @if (contactForm.get('conactName')?.hasError('maxlength')) {
              <small class="text-red-600">Máximo {{ maxLength.contactName }} caracteres</small>
            }
          </div>
          <div class="flex flex-col gap-1 col-span-2">
            <p-floatlabel variant="on">
              <input
                pInputText
                id="contactEmail"
                type="email"
                formControlName="contactEmail"
                [maxlength]="maxLength.contactEmail"
                fluid
                autocomplete="off"
                [invalid]="(contactForm.get('contactEmail')?.invalid ?? false) && (contactForm.get('contactEmail')?.touched ?? false)"
              />
              <label for="contactEmail">Email</label>
            </p-floatlabel>
            @if (contactForm.get('contactEmail')?.hasError('email') && contactForm.get('contactEmail')?.touched) {
              <small class="text-red-600">El formato del email no es correcto</small>
            }
            @if (contactForm.get('contactEmail')?.hasError('maxlength')) {
              <small class="text-red-600">Máximo {{ maxLength.contactEmail }} caracteres</small>
            }
          </div>
          <div class="flex flex-col gap-1 col-span-2">
            <p-floatlabel variant="on">
              <input 
                pInputText 
                id="contactPhone" 
                formControlName="contactPhone" 
                fluid 
                type="tel"
                inputmode="numeric"
                autocomplete="off"
                (input)="onContactPhoneInput($event)"
                [invalid]="(contactForm.get('contactPhone')?.invalid ?? false) && (contactForm.get('contactPhone')?.touched ?? false)"
                [maxlength]="maxLength.contactPhoneMasked"
                />
              <label for="contactPhone">Teléfono</label>
            </p-floatlabel>
            @if (contactForm.get('contactPhone')?.hasError('maxlength')) {
              <small class="text-red-600">Máximo {{ maxLength.contactPhone }} dígitos</small>
            }
            @if (contactForm.get('contactPhone')?.hasError('pattern') && contactForm.get('contactPhone')?.touched) {
              <small class="text-red-600">Solo se permiten números</small>
            }
          </div>
        </div>
        <div class="flex justify-end gap-3 mt-6">
          <p-button label="Cancelar" severity="secondary" (onClick)="cancelContactEdit()" />
          <p-button
            [label]="relationsLoading() ? 'Guardando...' : (editingContactId() ? 'Actualizar' : 'Guardar')"
            icon="pi pi-check"
            [loading]="relationsLoading()"
            (onClick)="saveContact()"
          />
        </div>
      </div>
    </div>
  }

  <!-- Modal confirmar eliminar dirección/contacto -->
  @if (deleteConfirmOpen()) {
    <div class="fixed inset-0 z-[1200] flex items-center justify-center">
      <div
        class="absolute inset-0 bg-black transition-opacity duration-200"
        [class.bg-opacity-40]="deleteConfirmShown()"
        [class.bg-opacity-0]="!deleteConfirmShown()"
        (click)="closeDeleteConfirm()"
      ></div>
      <div
        class="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6 z-10 transform transition-all duration-200"
        [class.opacity-100]="deleteConfirmShown()"
        [class.opacity-0]="!deleteConfirmShown()"
        [class.scale-100]="deleteConfirmShown()"
        [class.scale-95]="!deleteConfirmShown()"
      >
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-slate-900">
            @if (deleteTarget() === 'address') { Eliminar dirección }
            @if (deleteTarget() === 'contact') { Eliminar contacto }
            @if (deleteTarget() === 'attachment') { Eliminar archivo }
          </h3>
          <p-button icon="pi pi-times" [rounded]="true" [text]="true" severity="secondary" (onClick)="closeDeleteConfirm()" />
        </div>
        <p class="text-sm text-slate-600 mb-6">
          @if (deleteTarget() === 'address' && deleteAddressItem(); as addr) {
            ¿Eliminar la dirección {{ getAddressTypeLabel(addr.type) }} – {{ formatAddressStreet(addr) }}?
          }
          @if (deleteTarget() === 'contact' && deleteContactItem(); as con) {
            ¿Eliminar el contacto {{ con.conactName }} ({{ getContactTypeLabel(con.type) }})?
          }
          @if (deleteTarget() === 'attachment' && deleteAttachmentItem(); as file) {
            ¿Eliminar el archivo {{ file.name }}?
          }
        </p>
        <div class="flex justify-end gap-3">
          <p-button label="Cancelar" severity="secondary" (onClick)="closeDeleteConfirm()" [disabled]="deleteLoading()" />
          <p-button label="Eliminar" severity="danger" icon="pi pi-trash" [loading]="deleteLoading()" [disabled]="deleteLoading()" (onClick)="confirmDelete()" />
        </div>
      </div>
    </div>
  }

</div>
